import dayjs from 'dayjs'
import {http} from 'hiya-common/src/utils/axios'

import type {WorldCupTeamFlagKey} from '../../components/WorldCupBetNoticeModal/types'
import type {
  WorldCupBetSortDimension,
  WorldCupBettingPeriod,
  WorldCupBettingTeamItem,
} from '../../components/WorldCupBettingMain/types'
import type {WorldCupActivityGiftsProps} from '../../components/WorldCupActivityGifts/types'
import type {WorldCupNewGiftPackageProps} from '../../components/WorldCupNewGiftPackage/types'
import type {WorldCupActivityPeriod} from '../../components/WorldCupRankingList/types'
import {formatNextStageEffectiveAt} from '../../components/WorldCupBettingMain/api'
import {WORLD_CUP_TEAM_FLAG_KEYS} from '../../constant'
import type {WorldCupHalfSheetBettingRuleRow} from '../../components/WorldCupHalfSheetPopup/types'
import type {WorldCupChampionPredictPageProps} from './types'

/** 活动状态：0 未开始，2 正式期，3 回顾期 */
export const WORLD_CUP_PICK_ACTIVITY_STATUS = {
  notStarted: 0,
  formal: 2,
  review: 3,
} as const

/** 队伍列表排序：1 总注数倒序，2 赔率倒序，3 个人注数倒序 */
export const WORLD_CUP_PICK_SORT_TYPE = {
  hotTeam: 1,
  highOdds: 2,
  myBets: 3,
} as const

export const SORT_DIMENSION_TO_SORT_TYPE: Record<
  WorldCupBetSortDimension,
  number
> = {
  'Hot team': WORLD_CUP_PICK_SORT_TYPE.hotTeam,
  'High odds': WORLD_CUP_PICK_SORT_TYPE.highOdds,
  'My bets': WORLD_CUP_PICK_SORT_TYPE.myBets,
}

export const SORT_TYPE_TO_SORT_DIMENSION = {
  [WORLD_CUP_PICK_SORT_TYPE.hotTeam]: 'Hot team',
  [WORLD_CUP_PICK_SORT_TYPE.highOdds]: 'High odds',
  [WORLD_CUP_PICK_SORT_TYPE.myBets]: 'My bets',
}

export interface WorldCupPickRewardItem {
  id: number
  name: string
  view_icon: string
  toast_name: string
  reward_type: number
  tag: string
  privilege_id: number | null
  origin_id: number | null
  quantity: number
  reward_value: number
  has_show_effect: boolean
  days: number | null
}

export interface WorldCupPickTeamItem {
  team_id: number
  name: string
  flag_url: string
  total_votes: number
  odds: number
  user_votes: number
  is_eliminated: boolean
}

/** 投注阶段（规则页表格） */
export interface WorldCupPickBetStageItem {
  stage_no: number
  name: string
  start_date: string
  end_date: string
  votes_per_coin: number
}

export interface WorldCupPickHomepageRequest {
  sort_type: number
}

export interface WorldCupPickHomepageResponse {
  err_msg?: string
  is_allowed?: boolean
  /** 是否首次进入活动页 */
  is_first_enter?: boolean
  activity_status?: number
  countdown?: number
  cur_stage_no?: number
  cur_stage_start_time?: number
  cur_stage_end_time?: number
  cur_stage_votes_per_coin?: number
  next_stage_votes_per_coin?: number
  next_stage_start_time?: number
  user_coins?: number
  user_free_tickets?: number
  coin_bet_options?: number[]
  ticket_bet_options?: number[]
  pool_coins?: number
  teams?: WorldCupPickTeamItem[]
  champion_team_id?: number
  champion_team?: WorldCupPickTeamItem | null
  user_win?: boolean
  user_win_coins?: number
  invite_gift_tickets?: number
  invite_gift_rewards?: WorldCupPickRewardItem[]
  invite_success_count?: number
  event_gifts?: WorldCupPickRewardItem[]
  bet_stages?: WorldCupPickBetStageItem[]
  rank_start_time?: number
  rank_end_time?: number
}

export type NormalizedWorldCupPickHomepageData = {
  err_msg: string
  is_allowed: boolean
  is_first_enter: boolean
  activity_status: number
  countdown: number
  cur_stage_no: number
  cur_stage_start_time: number
  cur_stage_end_time: number
  cur_stage_votes_per_coin: number
  next_stage_votes_per_coin: number
  next_stage_start_time: number
  user_coins: number
  user_free_tickets: number
  coin_bet_options: number[]
  ticket_bet_options: number[]
  pool_coins: number
  teams: WorldCupPickTeamItem[]
  champion_team_id: number
  champion_team: WorldCupPickTeamItem | null
  user_win: boolean
  user_win_coins: number
  invite_gift_tickets: number
  invite_gift_rewards: WorldCupPickRewardItem[]
  invite_success_count: number
  event_gifts: WorldCupPickRewardItem[]
  bet_stages: WorldCupPickBetStageItem[]
  rank_start_time: number
  rank_end_time: number
}

export const EMPTY_WORLD_CUP_PICK_HOMEPAGE: NormalizedWorldCupPickHomepageData =
  {
    err_msg: '',
    is_allowed: true,
    is_first_enter: false,
    activity_status: WORLD_CUP_PICK_ACTIVITY_STATUS.review,
    countdown: 0,
    cur_stage_no: 0,
    cur_stage_start_time: 0,
    cur_stage_end_time: 0,
    cur_stage_votes_per_coin: 0,
    next_stage_votes_per_coin: 0,
    next_stage_start_time: 0,
    user_coins: 0,
    user_free_tickets: 0,
    coin_bet_options: [],
    ticket_bet_options: [],
    pool_coins: 0,
    teams: [],
    champion_team_id: 0,
    champion_team: null,
    user_win: false,
    user_win_coins: 0,
    invite_gift_tickets: 0,
    invite_gift_rewards: [],
    invite_success_count: 0,
    event_gifts: [],
    bet_stages: [],
    rank_start_time: 0,
    rank_end_time: 0,
  }

export function normalizeWorldCupPickHomepageResponse(
  res: WorldCupPickHomepageResponse
): NormalizedWorldCupPickHomepageData {
  return {
    err_msg: res?.err_msg ?? '',
    is_allowed: res?.is_allowed ?? true,
    is_first_enter: res?.is_first_enter ?? false,
    activity_status:
      res?.activity_status ?? WORLD_CUP_PICK_ACTIVITY_STATUS.formal,
    countdown: res?.countdown ?? 0,
    cur_stage_no: res?.cur_stage_no ?? 0,
    cur_stage_start_time: res?.cur_stage_start_time ?? 0,
    cur_stage_end_time: res?.cur_stage_end_time ?? 0,
    cur_stage_votes_per_coin: res?.cur_stage_votes_per_coin ?? 0,
    next_stage_votes_per_coin: res?.next_stage_votes_per_coin ?? 0,
    next_stage_start_time: res?.next_stage_start_time ?? 0,
    user_coins: res?.user_coins ?? 0,
    user_free_tickets: res?.user_free_tickets ?? 0,
    coin_bet_options: res?.coin_bet_options ?? [],
    ticket_bet_options: res?.ticket_bet_options ?? [],
    pool_coins: res?.pool_coins ?? 0,
    teams: res?.teams ?? [],
    champion_team_id: res?.champion_team_id ?? 0,
    champion_team: res?.champion_team ?? null,
    user_win: res?.user_win ?? false,
    user_win_coins: res?.user_win_coins ?? 0,
    invite_gift_tickets: res?.invite_gift_tickets ?? 0,
    invite_gift_rewards: res?.invite_gift_rewards ?? [],
    invite_success_count: res?.invite_success_count ?? 0,
    event_gifts: res?.event_gifts ?? [],
    bet_stages: res?.bet_stages ?? [],
    rank_start_time: res?.rank_start_time ?? 0,
    rank_end_time: res?.rank_end_time ?? 0,
  }
}

export function mapBetStagesToBettingRuleRows(
  stages: WorldCupPickBetStageItem[]
): WorldCupHalfSheetBettingRuleRow[] {
  return stages.map((item) => ({
    key: item.stage_no,
    stageLabel: String(item.stage_no),
    timeLabel: `${item.start_date} - ${item.end_date}`,
    coinMultiplier: 1,
    ticketMultiplier: item.votes_per_coin,
  }))
}

export async function postWorldCupPickHomepage(
  params: WorldCupPickHomepageRequest
): Promise<NormalizedWorldCupPickHomepageData> {
  const res = await http.post<{data: WorldCupPickHomepageResponse}>(
    '/live/activity/world_cup_pick_homepage',
    {...params},
    {
      store: window.store,
    }
  )
  return normalizeWorldCupPickHomepageResponse(res.data.data)
}

export function getTeamFlagKeyById(teamId: number): WorldCupTeamFlagKey {
  const index = teamId - 1
  return WORLD_CUP_TEAM_FLAG_KEYS[index] ?? 'mexico'
}

function formatStageDate(timestampSec: number): string {
  if (!timestampSec) {
    return ''
  }
  return dayjs(timestampSec * 1000).format('MMMM D')
}

function mapPickTeamToBettingItem(
  item: WorldCupPickTeamItem,
  index: number
): WorldCupBettingTeamItem {
  return {
    id: String(item.team_id),
    teamFlagKey: getTeamFlagKeyById(item.team_id),
    teamName: item.name,
    odds: item.odds,
    platformBetTicketCount: item.total_votes,
    matchOrder: index + 1,
    betTicketCount: item.user_votes,
    status: item.is_eliminated ? 'eliminated' : 'active',
  }
}

function mapRewardToGiftPackageItem(
  reward: WorldCupPickRewardItem,
  index: number
): WorldCupNewGiftPackageProps['gifts'][number] {
  return {
    id: String(reward.id ?? index),
    rewardConfigId: reward.id,
    iconUrl: reward.view_icon,
    name: reward?.toast_name?.split('*')?.[0] || '',
    rewardType: reward.reward_type,
    quantity: reward.quantity,
    showPlayIcon: reward.has_show_effect,
  }
}

function mapRewardToActivityGiftItem(
  reward: WorldCupPickRewardItem,
  index: number
): WorldCupActivityGiftsProps['gifts'][number] {
  return {
    id: String(reward.id ?? index),
    rewardConfigId: reward.id,
    iconUrl: reward.view_icon,
    nameLine: reward?.toast_name?.split('*')?.[0] || '',
    rewardType: reward.reward_type,
    quantity: reward.quantity,
    showPlayIcon: reward.has_show_effect,
  }
}

function mapActivityStatusToPeriod(
  activityStatus: number
): WorldCupBettingPeriod {
  return activityStatus === WORLD_CUP_PICK_ACTIVITY_STATUS.review
    ? 'review'
    : 'formal'
}

function mapActivityStatusToRankingPeriod(
  activityStatus: number
): WorldCupActivityPeriod {
  return activityStatus === WORLD_CUP_PICK_ACTIVITY_STATUS.review
    ? 'review'
    : 'formal'
}

export function mapHomepageToPageProps(
  data: NormalizedWorldCupPickHomepageData,
  sortType: number,
  callbacks?: {
    onSortDimensionChange?: (dimension: WorldCupBetSortDimension) => void
    onBetSuccess?: WorldCupChampionPredictPageProps['bettingMain']['onBetSuccess']
  }
): WorldCupChampionPredictPageProps {
  const period = mapActivityStatusToPeriod(data.activity_status)
  const championTeam = data.champion_team
  const sortDimension = SORT_TYPE_TO_SORT_DIMENSION[sortType] ?? 'Hot team'

  return {
    prizePoolValue: data.pool_coins,
    countdownLeftSeconds: data.countdown,
    coinBalance: data.user_coins,
    ticketBalance: data.user_free_tickets,
    newGiftPackage: {
      gifts: data.invite_gift_rewards.map(mapRewardToGiftPackageItem),
      inviteCount: String(data.invite_success_count),
    },
    bettingMain: {
      period,
      isFirstEnter: data.is_first_enter,
      phase: data.cur_stage_no,
      phaseStartDate: dayjs(data.cur_stage_start_time * 1000).format('MM.DD'),
      phaseEndDate: dayjs(data.cur_stage_end_time * 1000).format('MM.DD'),
      exchangeTicketCount: data.cur_stage_votes_per_coin,
      teams: data.teams.map(mapPickTeamToBettingItem),
      sortDimension,
      serverSorted: true,
      coinBetOptions: data.coin_bet_options,
      ticketBetOptions: data.ticket_bet_options,
      accountCoinBalance: data.user_coins,
      ticketBalance: data.user_free_tickets,
      nextRateEffectiveAt: formatNextStageEffectiveAt(
        data.next_stage_start_time
      ),
      championTeamId: data.champion_team_id,
      championTeamFlagKey: championTeam
        ? getTeamFlagKeyById(championTeam.team_id)
        : undefined,
      championTeamName: championTeam?.name,
      rewardCoinAmount: data.user_win ? data.user_win_coins : 0,
      onSortDimensionChange: callbacks?.onSortDimensionChange,
      onBetSuccess: callbacks?.onBetSuccess,
    },
    activityGifts: {
      gifts: data.event_gifts.map(mapRewardToActivityGiftItem),
    },
    rankingList: {
      activityPeriod: mapActivityStatusToRankingPeriod(data.activity_status),
      rankStartTime: data.rank_start_time,
      rankEndTime: data.rank_end_time,
    },
  }
}
